import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockResetIcon from '@mui/icons-material/LockReset';
import PaidIcon from '@mui/icons-material/Paid';
import GroupIcon from '@mui/icons-material/Group';
import ScienceIcon from '@mui/icons-material/Science';

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const progressValue = (part, total) => (total ? (part / total) * 100 : 0);
const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');
const formatRole = (role) => {
  if (!role) return '-';
  return role.charAt(0).toUpperCase() + role.slice(1);
};

const SummaryCard = ({ loading, title, value, subtitle, Icon, rows = [] }) => (
  <div className="UserSummary recoverySummaryCard">
    <div className="summaryContainer">
      <div className="summary">
        <div className="number">{loading ? <CircularProgress size={30} /> : value}</div>
        <div className="text">{title}</div>
        {subtitle ? <div className="recoverySummarySubtitle">{subtitle}</div> : null}
      </div>
      <div className="icon">
        <Icon color="primary" fontSize="large" />
      </div>
    </div>
    <Divider />
    {rows.map((row) => (
      <div className="activeUsersBar" key={row.label}>
        <div className="barTexts">
          <div className="barTitle">{row.label}:</div>
          <div>{loading ? <CircularProgress size={10} /> : row.value}</div>
        </div>
        {row.progressTotal ? (
          <LinearProgress
            variant="determinate"
            value={progressValue(row.progressValue, row.progressTotal)}
            title={formatPercent(progressValue(row.progressValue, row.progressTotal))}
          />
        ) : null}
      </div>
    ))}
  </div>
);

const RecoveryUsersTable = ({ title, users, loading, emptyMessage }) => (
  <div className="pdfDashboardTableSection recoveryTableSection">
    <div className="pdfDashboardTableTitle recoveryTableTitle">{title}</div>
    <div className="recoveryTableScroll">
      {loading ? (
        <div className="recoveryEmptyState">
          <CircularProgress size={28} />
        </div>
      ) : users.length ? (
        <div className="pdfTableGrid recoveryUsersTableGrid">
          <div className="pdfTableGridHeader">
            <div className="pdfTableGridCell">#</div>
            <div className="pdfTableGridCell">Name</div>
            <div className="pdfTableGridCell">Email</div>
            <div className="pdfTableGridCell">Role</div>
            <div className="pdfTableGridCell">Source</div>
            <div className="pdfTableGridCell">Recovered</div>
            <div className="pdfTableGridCell">Reset At</div>
            <div className="pdfTableGridCell">State</div>
          </div>
          {users.map((user, index) => (
            <div className="pdfTableGridRow" key={user._id || `${user.email}-${index}`}>
              <div className="pdfTableGridCell">{index + 1}</div>
              <div className="pdfTableGridCell">{user.name || '-'}</div>
              <div className="pdfTableGridCell">{user.email || '-'}</div>
              <div className="pdfTableGridCell">{formatRole(user.role)}</div>
              <div className="pdfTableGridCell">{user.recoverySourceLabel || '-'}</div>
              <div className="pdfTableGridCell">{formatDate(user.recoveredAt)}</div>
              <div className="pdfTableGridCell">{formatDate(user.resetCompletedAt)}</div>
              <div className="pdfTableGridCell">
                <span className={`recoveryStateBadge ${user.resetCompletedAt ? 'completed' : 'pending'}`}>
                  {user.resetCompletedAt ? 'Completed' : 'Pending'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="recoveryEmptyState">{emptyMessage}</div>
      )}
    </div>
  </div>
);

const AdminDisaster = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const getDisasterRecoveryStats = async () => {
      setLoading(true);
      try {
        const response = await axios({
          method: 'GET',
          url: '/api/v1/admin/getDisasterRecoveryStats',
        });
        setStats(response.data.data);
      } catch (err) {
        console.log('Error->', err?.response?.data?.message || err?.message);
        setStats(null);
      }
      setLoading(false);
    };
    getDisasterRecoveryStats();
  }, []);

  const summary = stats?.summary || {
    trackedRecoveredUsers: 0,
    totalRecoveredUsers: 0,
    recoveryTestUsers: 0,
    activeRecoveredUsers: 0,
    confirmedRecoveredUsers: 0,
    completedResetCount: 0,
    pendingResetCount: 0,
    completionRate: 0,
    paidRecoveredUsers: 0,
    paidCompletedResetCount: 0,
    paidPendingResetCount: 0,
    paidCompletionRate: 0,
    firstRecoveredAt: null,
    latestRecoveredAt: null,
    sourceBreakdown: [],
    roleBreakdown: [],
  };

  const pendingUsers = stats?.users?.pendingUsers || [];
  const completedUsers = stats?.users?.completedUsers || [];
  const testUsers = stats?.users?.testUsers || [];

  const sourceRows = summary.sourceBreakdown
    .filter((row) => row.total > 0)
    .map((row) => ({
      label: row.label,
      value: `${row.total} total / ${row.completed} reset`,
      progressValue: row.completed,
      progressTotal: row.total,
    }));

  const roleRows = summary.roleBreakdown
    .filter((row) => row.count > 0)
    .map((row) => ({
      label: formatRole(row.role),
      value: row.count,
      progressValue: row.count,
      progressTotal: summary.trackedRecoveredUsers,
    }));

  return (
    <div className="adminDashboard">
      <h1>Disaster Recovery</h1>
      <div className="adminDashboardContainer recoveryDashboardLayout">
        <Alert severity="warning" className="recoveryInfoAlert">
          This tab tracks restored accounts using recovery metadata. Completion rates exclude test accounts and count only
          users who have reset their password after recovery.
        </Alert>

        <div className="flex">
          <SummaryCard
            loading={loading}
            title="Recovered Customers"
            value={summary.trackedRecoveredUsers}
            subtitle={`${summary.totalRecoveredUsers} total restored accounts`}
            Icon={WarningAmberIcon}
            rows={[
              {
                label: 'Active',
                value: summary.activeRecoveredUsers,
                progressValue: summary.activeRecoveredUsers,
                progressTotal: summary.trackedRecoveredUsers,
              },
              {
                label: 'Email Confirmed',
                value: summary.confirmedRecoveredUsers,
                progressValue: summary.confirmedRecoveredUsers,
                progressTotal: summary.trackedRecoveredUsers,
              },
              {
                label: 'Test Accounts',
                value: summary.recoveryTestUsers,
              },
            ]}
          />

          <SummaryCard
            loading={loading}
            title="Password Resets Complete"
            value={summary.completedResetCount}
            subtitle={`${summary.pendingResetCount} still pending`}
            Icon={LockResetIcon}
            rows={[
              {
                label: 'Recovery Rate',
                value: formatPercent(summary.completionRate),
                progressValue: summary.completedResetCount,
                progressTotal: summary.trackedRecoveredUsers,
              },
              {
                label: 'First Recovered',
                value: formatDate(summary.firstRecoveredAt),
              },
              {
                label: 'Latest Recovered',
                value: formatDate(summary.latestRecoveredAt),
              },
            ]}
          />

          <SummaryCard
            loading={loading}
            title="Paid Recovery"
            value={summary.paidRecoveredUsers}
            subtitle={`${summary.paidPendingResetCount} paid users pending`}
            Icon={PaidIcon}
            rows={[
              {
                label: 'Paid Resets Complete',
                value: summary.paidCompletedResetCount,
                progressValue: summary.paidCompletedResetCount,
                progressTotal: summary.paidRecoveredUsers,
              },
              {
                label: 'Paid Recovery Rate',
                value: formatPercent(summary.paidCompletionRate),
                progressValue: summary.paidCompletedResetCount,
                progressTotal: summary.paidRecoveredUsers,
              },
            ]}
          />

          <SummaryCard loading={loading} title="Recovery Sources" value={sourceRows.length} subtitle="Sources with restored users" Icon={ScienceIcon} rows={sourceRows} />

          <SummaryCard
            loading={loading}
            title="Recovered Roles"
            value={summary.roleBreakdown.reduce((acc, row) => acc + row.count, 0)}
            subtitle="Role mix across restored customers"
            Icon={GroupIcon}
            rows={roleRows}
          />
        </div>

        <div className="recoveryTables">
          <RecoveryUsersTable
            title={`Pending Password Resets (${pendingUsers.length})`}
            users={pendingUsers}
            loading={loading}
            emptyMessage="No restored users are currently waiting on a password reset."
          />
          <RecoveryUsersTable
            title={`Completed Password Resets (${completedUsers.length})`}
            users={completedUsers}
            loading={loading}
            emptyMessage="No restored users have completed a password reset yet."
          />
          {testUsers.length ? (
            <RecoveryUsersTable
              title={`Recovery Test Accounts (${testUsers.length})`}
              users={testUsers}
              loading={loading}
              emptyMessage="No recovery test accounts found."
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminDisaster;
